/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import React, { useContext, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

import { useConfigContext } from '../ConfigContext/ConfigContext';
import SchemaChecker from './SchemaChecker';
import { NoConfigurationException } from './exceptions';
import { minUIDSupportVersion } from '../../queries';
import { compareSchemaVersions } from '../../utils/compareSchema';

export const GraphqlSchemaContext = React.createContext();

const LOADING_STATE = ['loading', null, []];

const GraphqlSchemaContextProvider = props => {
    const [configs] = useConfigContext();
    const [status, setStatus] = useState(LOADING_STATE); // Possible values: loading, error, done

    const checkedSchemaMerger = useCallback(
        resolvedFeatures => config => {
            for (let { schemaChecker, features } of resolvedFeatures) {
                // find the first that belongs to the configs instanceUrl, only consider those that retrieved features
                if (!features || schemaChecker.instanceUrl !== config.instanceUrl) {
                    continue;
                }

                const { compatibleVersion: schemaVersion } = features;
                const supportUID =
                    schemaVersion &&
                    config.enableUIDSupport &&
                    (config.enableUIDSupport === 'true' || config.enableUIDSupport === true) &&
                    compareSchemaVersions(schemaVersion, minUIDSupportVersion) > -1;

                return { ...config, schemaVersion, supportUID };
            }

            return props.removeInvalidConfigs ? null : config;
        },
        [props.removeInvalidConfigs]
    );

    const fetchSchemas = useCallback(async () => {
        try {
            // Throw if no config is available
            if (configs.length === 0 && props.failOnNoConfig) {
                throw new NoConfigurationException();
            }

            // Reduce configuration, to check every endpoint independently of the storeview only once
            const endpoints = configs.reduce((a, c) => {
                a[c.instanceUrl] = {
                    gqlClient: c.gqlClient,
                    configName: c.name ? c.name : c.instanceUrl
                };
                return a;
            }, {});

            // Initialize SchemaChecker classes for every config
            const schemaCheckers = Object.keys(endpoints).map(
                e => new SchemaChecker(e, endpoints[e].gqlClient, endpoints[e].configName)
            );

            // Fetch all schemas at the same time, will throw if any endpoint is not available or returns invalid schema
            const resolvedFeatures = await Promise.all(
                schemaCheckers.map(schemaChecker => {
                    let promise = schemaChecker.getFeatures();

                    if (configs.length > 1 || !props.failOnNoConfig) {
                        // if we have multiple endpoints don't fail immediately when one is invalid
                        promise = promise.catch(err => console.error(err.toString()));
                    }

                    return promise.then(features => ({ schemaChecker, features }));
                })
            );

            // Check if all schemas are compatible, will throw if not
            const checkedConfigs = configs.map(checkedSchemaMerger(resolvedFeatures)).filter(config => !!config);

            // Throw if no config is valid
            if (checkedConfigs.length === 0 && props.failOnNoConfig) {
                throw new NoConfigurationException();
            }

            setStatus(['done', null, checkedConfigs]);
        } catch (err) {
            console.error(err.toString());
            setStatus(['error', err, []]);
        }
    }, [configs, props.failOnNoConfig, checkedSchemaMerger]);

    useEffect(() => {
        setStatus(LOADING_STATE);
        fetchSchemas();
    }, [fetchSchemas]);

    return <GraphqlSchemaContext.Provider value={status}>{props.children}</GraphqlSchemaContext.Provider>;
};

GraphqlSchemaContextProvider.propTypes = {
    removeInvalidConfigs: PropTypes.bool,
    failOnNoConfig: PropTypes.bool
};

GraphqlSchemaContextProvider.defaultProps = {
    removeInvalidConfigs: false,
    failOnNoConfig: true
};

export default GraphqlSchemaContextProvider;

export const useGraphqlSchemaContext = () => useContext(GraphqlSchemaContext);
